import { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';

interface EhPaymentErrorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  error?: string;
  onRetry?: () => void;
}

const DEFAULT_ERROR = 'An unexpected error occurred during payment processing.';

export function EhPaymentErrorDialog({  
  open,
  onOpenChange,
  error = DEFAULT_ERROR,
  onRetry
}: EhPaymentErrorDialogProps) {
  const [isOpen, setIsOpen] = useState(open);
  
  useEffect(() => {
    setIsOpen(open);
  }, [open, error]);
  
  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    onOpenChange(newOpen);
  };

  const mailtoLink = `mailto:support@eveninghoney.ai?subject=Payment%20Error&body=${
    encodeURI(`Hi, I'm having trouble with my subscription payment. Error details: ${error}`)
  }`;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md bg-gradient-to-br from-zinc-900 to-zinc-950 border-zinc-800">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2 mb-2">
            <AlertTriangle className="h-5 w-5 text-rose-500" /> 
            <div>Payment Error</div>
          </DialogTitle>
          <DialogDescription className="text-zinc-300">
            {error || DEFAULT_ERROR}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-3 text-sm text-zinc-300">
          <p>
            <span>If this problem persists and you don&apos;t see your account updated after payment, please contact our support team for assistance at </span>
            <a href={mailtoLink} className="text-rose-400 hover:text-rose-300">support@eveninghoney.ai</a>
          </p>
        </div>
        
        <DialogFooter className="flex flex-col sm:flex-row gap-4 sm:gap-2 mt-2">
          {onRetry && (
            <Button
              type="button"
              className="bg-slate-600 hover:bg-slate-700"
              onClick={() => {
                onRetry();
                handleOpenChange(false);
              }}
            >
              Try Again
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 